import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BackButton from "../../components/BackButton";
import { allowedViews, pathHome, pathLeagueView, type View } from "../../../config/routes";
import { AdPositionEnum, LanguageLocaleEnum, League, PlayerStatisticsListV2, SportEnum, StatisticKeyEnum } from "../../../client/api";
import { selectLanguageOrDefault, selectSportsType } from "../../../selectors";
import { showTranslated, translate } from "../../../config/translator";
import Loading from "../../components/Loading";
import RoundedRectangle from "../../components/RoundedRectangle";
import TabletLayoutPage, { TabletContainer } from "../../components/Layout/TabletLayout";
import { MainColumn, RightRail, TwoColumnGrid } from "../../components/Layout/TwoColumn";
import SegmentedTabs from "../../components/SegmentedTabs";
import Table from "../../components/Table";
import StoreBanner from "../../components/StoreBanner";
import MatchTile from "../../components/Match/MatchTile";
import { AdSlot } from "../../components/AdSlot";
import { fetchLeagueById, fetchLeaguesBySubCategory } from "../../../store/actions/leagueActions";
import { AppDispatch, RootState } from "../../../store";
import { MatchSmallElement } from "../../../config/types";
import MatchdaySelector from "../../components/MatchdaySelector";
import LeagueSelector from "../../components/LeagueSelector";
import DateNavigator from "../../components/DateNavigator";
import TOTW from "../../components/TOTW";
import { useLeagueBaseData } from "../../../hooks/useLeagueBaseData";
import { useLeagueMatchday } from "../../../hooks/useLeagueMatchday";
import { useCupMatches } from "../../../hooks/useCupMatches";
import LeagueStatisticsPlayer from "./LeagueStatisticsPlayer";
import StatisticsPanels from "./LeagueStatisticsPlayer/StatisticsPanels";
import PlayerStatisticPanel, { PlayerStatData } from "./LeagueStatisticsPlayer/PlayerStatisticPanel";
import { isPremium } from "../../../config/utils";

const LeaguePage: React.FC = () => {
  const { leagueId: leagueIdParam, view: viewParam } = useParams<{
    leagueId?: string;
    view?: string;
  }>();

  const leagueId = useMemo(() => {
    const n = leagueIdParam ? parseInt(leagueIdParam, 10) : NaN;
    return Number.isFinite(n) ? n : undefined;
  }, [leagueIdParam]);

  const view: View | undefined = useMemo(
    () => (viewParam && allowedViews.includes(viewParam as View) ? (viewParam as View) : undefined),
    [viewParam]
  );

  const l = useSelector(selectLanguageOrDefault);
  const sports = useSelector(selectSportsType);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    league,
    allLeagues,
    user,
    aoi,
    isCup,
    hasTotwRestriction,
    isFollowing,
    handleFollowToggle
  } = useLeagueBaseData(leagueId);
  const leagueStatus = useSelector((s: RootState) => s.leagues.status);
  const leagueError = useSelector((s: RootState) => s.leagues.error);

  const {
    matchday,
    setMatchday,
    matchdayItems,
    totwMatchdayItems,
    currentMatchdayLabel
  } = useLeagueMatchday({
    leagueId,
    isCup,
    sports,
    leagueMatchDays: league?.matchDays,
    currentMatchDay: league?.currentMatchDay,
    l,
    hasTotwRestriction
  });

  const {
    selectedCupLeagueId,
    availableDateObjects,
    selectedDate,
    selectedDateLabel,
    displayMatches,
    handleMatchesLeagueChange,
    handlePrevDate,
    handleNextDate,
    handleDateSelect
  } = useCupMatches({
    isCup,
    leagueId,
    leagueSubCategoryId: league?.subCategory?.id,
    aoi,
    sports,
    language: l
  });

  const subCategoryId = league?.subCategory?.id;
  const cupLeagues = useMemo(() => {
    if (!isCup || !subCategoryId) return [];
    return allLeagues.filter(
      (leagueItem) => leagueItem.subCategory?.id === subCategoryId && leagueItem.active
    );
  }, [isCup, subCategoryId, allLeagues]);

  const leagueSelectorItems = useMemo(
    () => cupLeagues.map((leagueItem) => ({
      key: leagueItem.id,
      label: showTranslated(leagueItem.name, l)
    })),
    [cupLeagues, l]
  );

  const leagueSelectorItemsWithAll = useMemo(
    () => [
      { key: "all" as const, label: translate("allGroups", l) },
      ...cupLeagues.map((leagueItem) => ({
        key: leagueItem.id,
        label: showTranslated(leagueItem.name, l)
      }))
    ],
    [cupLeagues, l]
  );

  const handleLeagueChange = useCallback((newLeagueId: number | string | undefined) => {
    if (typeof newLeagueId === "number" && newLeagueId !== leagueId) {
      navigate(pathLeagueView(newLeagueId, view || "table"));
    }
  }, [leagueId, navigate, view]);

  useEffect(() => {
    if (!leagueId) return;
    fetchLeagueById(dispatch, leagueId);
  }, [leagueId, dispatch]);

  useEffect(() => {
    if (isCup && league?.subCategory?.id && cupLeagues.length <= 1) {
      fetchLeaguesBySubCategory(dispatch, league.subCategory.id);
    }
  }, [isCup, league?.subCategory?.id, cupLeagues.length, dispatch]);

  const currentLeagueMatchDay = league?.currentMatchDay;

  useEffect(() => {
    if (
      hasTotwRestriction &&
      view === "totw" &&
      currentLeagueMatchDay &&
      matchday !== currentLeagueMatchDay
    ) {
      setMatchday(currentLeagueMatchDay);
    }
  }, [hasTotwRestriction, view, currentLeagueMatchDay, matchday, setMatchday]);

  const [playerStatsData, setPlayerStatsData] = useState<PlayerStatisticsListV2 | null>(null);
  const [premiumUserKey, setPremiumUserKey] = useState(0);
  const [dataUpdateKey, setDataUpdateKey] = useState(0);

  const isPremiumUser = useMemo(() => isPremium(user), [user]);

  const handlePlayerStatsLoaded = useCallback((data: PlayerStatisticsListV2) => {
    setPlayerStatsData(data);
    setDataUpdateKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    setPremiumUserKey((prev) => prev + 1);
  }, [isPremiumUser]);

  const extractStatisticData = useCallback((
    statisticKey: StatisticKeyEnum,
    limit = 3
  ): PlayerStatData[] => {
    if (!playerStatsData) return [];
    const stat = playerStatsData.find((s) => s.statistic.key === statisticKey);
    if (!stat) return [];
    return stat.playerStatistics.slice(0, limit).map(({ player, value, placement }) => ({
      player,
      value,
      placement,
    }));
  }, [playerStatsData]);

  const shouldBlurPremiumContent = hasTotwRestriction && !isPremiumUser;

  const evaluationData = useMemo(
    () => extractStatisticData(StatisticKeyEnum.PLAYER_RATING),
    [extractStatisticData]
  );

  const topScorerData = useMemo(
    () => extractStatisticData(StatisticKeyEnum.PLAYER_MOST_GOALS),
    [extractStatisticData]
  );

  const mostAssistData = useMemo(
    () => extractStatisticData(StatisticKeyEnum.PLAYER_MOST_ASSISTS),
    [extractStatisticData]
  );

  const mostMinutesData = useMemo(
    () => extractStatisticData(StatisticKeyEnum.PLAYER_MOST_MINUTES),
    [extractStatisticData]
  );

  if (typeof leagueId !== "number") {
    return <Navigate to={pathHome()} replace />;
  }

  if (!view) {
    return <Navigate to={pathLeagueView(leagueId, "table")} replace />;
  }

  if (leagueStatus === "loading" && !league) {
    return <Loading />;
  }

  if (leagueError) {
    return (
      <div className="LeaguePage w-full px-6 xl:px-8 mb-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-sm font-semibold text-red-800 mb-2">Error</h2>
            <p className="text-red-700">{translate("serverError", l)}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="LeaguePage w-full px-6 xl:px-8 mb-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">League not found</h2>
            <p className="text-gray-700">The requested league could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  const isTotwAvailable = !isCup && league.sports !== SportEnum.HOCKEY;

  if (view === "totw" && !isTotwAvailable) {
    return <Navigate to={pathLeagueView(leagueId, "table")} replace />;
  }

  const isTableViewVisible = !view || view === "table";
  const isMatchesViewVisible = view === "matches";
  const isTotwViewVisible = isTotwAvailable && view === "totw";
  const isTeamStatsVisible = view === "teamstats";

  return (
    <TabletLayoutPage>
      <TabletContainer className="LeaguePage">
        <TwoColumnGrid>
          <MainColumn>
            <div className="flex flex-col relative pt-0">
              <LeagueHeader
                league={league}
                isCup={isCup}
                l={l}
                onBack={() => navigate(-1)}
                canFollow={Boolean(user && leagueId)}
                isFollowing={isFollowing}
                onFollowToggle={handleFollowToggle}
              />

              <div className="block xl:hidden space-y-6 mb-6">
                {(evaluationData.length > 0 || shouldBlurPremiumContent) && (
                  <PlayerStatisticPanel
                    titleKey="evaluation"
                    players={evaluationData}
                    formatAsDecimal
                    shouldBlur={shouldBlurPremiumContent}
                  />
                )}
                {topScorerData.length > 0 && (
                  <PlayerStatisticPanel
                    titleKey="topScorerTitle"
                    players={topScorerData}
                  />
                )}
              </div>

              <SegmentedTabs
                className="justify-start w-full pt-3 pb-6 mt-1 xl:pt-2 xl:pb-4 xl:mt-0"
                activeKey={view ?? "table"}
                replace
                items={[
                  {
                    key: "table",
                    label: translate("table", l),
                    to: leagueId ? pathLeagueView(leagueId, "table") : "#"
                  },
                  {
                    key: "matches",
                    label: translate("matches", l),
                    to: leagueId ? pathLeagueView(leagueId, "matches") : "#"
                  },
                  ...(!isTotwAvailable ? [] : [{
                    key: "totw",
                    label: "TOTW",
                    to: leagueId ? pathLeagueView(leagueId, "totw") : "#"
                  }]),
                  {
                    key: "teamstats",
                    label: "Team Stats",
                    to: leagueId ? pathLeagueView(leagueId, "teamstats") : "#"
                  }
                ]}
              />

              <LeagueTableView
                isVisible={isTableViewVisible}
                isCup={isCup}
                leagueSelectorItems={leagueSelectorItems}
                leagueId={leagueId}
                onLeagueChange={handleLeagueChange}
                l={l}
              />

              <LeagueMatchesView
                isVisible={isMatchesViewVisible}
                isCup={isCup}
                leagueSelectorItemsWithAll={leagueSelectorItemsWithAll}
                selectedCupLeagueId={selectedCupLeagueId}
                leagueId={leagueId}
                onMatchesLeagueChange={handleMatchesLeagueChange}
                availableDateObjects={availableDateObjects}
                selectedDate={selectedDate}
                selectedDateLabel={selectedDateLabel}
                onPrevDate={handlePrevDate}
                onNextDate={handleNextDate}
                onDateSelect={handleDateSelect}
                matchday={matchday}
                onMatchdayChange={(value) => setMatchday(value || 1)}
                matchdayItems={matchdayItems}
                displayMatches={displayMatches}
                l={l}
              />

              <LeagueTotwView
                isVisible={isTotwViewVisible}
                hasTotwRestriction={hasTotwRestriction}
                currentMatchdayLabel={currentMatchdayLabel}
                matchday={matchday}
                onMatchdayChange={(value) => setMatchday(value || 1)}
                totwMatchdayItems={totwMatchdayItems}
                l={l}
                leagueId={leagueId}
              />

              <LeagueStatsView
                isVisible={isTeamStatsVisible}
                l={l}
              />

              <div className="block xl:hidden space-y-6 mb-6">
                {(mostAssistData.length > 0 || shouldBlurPremiumContent) && (
                  <PlayerStatisticPanel
                    titleKey="assists"
                    players={mostAssistData}
                    shouldBlur={shouldBlurPremiumContent}
                  />
                )}
                {mostMinutesData.length > 0 && (
                  <PlayerStatisticPanel
                    titleKey="mostMinutes"
                    players={mostMinutesData}
                  />
                )}
              </div>

              <LeagueStatisticsPlayer
                leagueId={leagueId}
                onDataLoaded={handlePlayerStatsLoaded}
                isPremiumUser={isPremiumUser}
              />

              <div className="mb-4">
                <AdSlot
                  type={AdPositionEnum.LEAGUE_SCREEN_BANNER}
                  className="w-full"
                />
              </div>
            </div>
          </MainColumn>
          <RightRail className="gap-4">
            <StatisticsPanels 
              key={`stats-${isPremiumUser ? 'pro' : 'free'}-${premiumUserKey}-${dataUpdateKey}`}
              playerStats={playerStatsData}
              isPremiumUser={isPremiumUser}
              hasRestriction={hasTotwRestriction}
            />
          </RightRail>
        </TwoColumnGrid>
      </TabletContainer>
    </TabletLayoutPage>
  );
};

export default LeaguePage;

type LeagueHeaderProps = {
  league: League | null;
  isCup: boolean;
  l: LanguageLocaleEnum;
  onBack: () => void;
  canFollow: boolean;
  isFollowing: boolean;
  onFollowToggle: () => void;
};

const LeagueHeader: React.FC<LeagueHeaderProps> = ({
                                                     league,
                                                     isCup,
                                                     l,
                                                     onBack,
                                                     canFollow,
                                                     isFollowing,
                                                     onFollowToggle
                                                   }) => (
  <RoundedRectangle
    className="league-header-card mb-4 bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pt-20"
    hasBorder
    hasTitle={false}
  >
    <BackButton onClick={onBack} />
    {league ? (
      <div className="flex items-center gap-4 px-6 xl:px-8 pb-6">
        <div className="flex flex-col">
                    <span className="text-base font-medium text-font dark:text-font-dark">
                        {isCup && league.subCategory ? showTranslated(league.subCategory.name, l) : showTranslated(league.name, l)}
                    </span>
          <span className="text-sm font-normal text-font-600 dark:text-font-600-dark">
                        {league.areasOfInterest?.map((aoiItem) => showTranslated(aoiItem.name, l)).join(" | ") || ""}
                    </span>
        </div>
        <div className="flex-1" />
        {canFollow && (
          <button
            type="button"
            onClick={onFollowToggle}
            className={`rounded-full h-9 py-1 px-3 flex justify-center items-center gap-1 transition-colors ${
              isFollowing
                ? "bg-primary-50 dark:bg-primary-50-dark text-font-300 dark:text-font-300-dark"
                : "bg-bg-50 dark:bg-bg-50-dark text-primary dark:text-primary"
            }`}
          >
            {translate(isFollowing ? "following" : "follow", l)}
          </button>
        )}
      </div>
    ) : (
      <span className="text-font-600 dark:text-font-600-dark px-6 xl:px-8 pb-6">
                {translate("loading", l)}
            </span>
    )}
  </RoundedRectangle>
);

type LeagueTableViewProps = {
  isVisible: boolean;
  isCup: boolean;
  leagueSelectorItems: { key: number; label: string }[];
  leagueId?: number;
  onLeagueChange: (value: number | string | undefined) => void;
  l: LanguageLocaleEnum;
};

const LeagueTableView: React.FC<LeagueTableViewProps> = ({
                                                           isVisible,
                                                           isCup,
                                                           leagueSelectorItems,
                                                           leagueId,
                                                           onLeagueChange,
                                                           l
                                                         }) => {
  if (!isVisible) return null;
  return (
    <div>
      {isCup && leagueSelectorItems.length > 1 && (
        <div className="mb-3 flex justify-start">
          <div className="w-auto min-w-64">
            <LeagueSelector
              value={leagueId}
              onChange={onLeagueChange}
              items={leagueSelectorItems}
              placeholder={translate("select", l)}
              transparent
              withoutChoose
              containerClassName="relative overflow-hidden h-9 flex-shrink-0 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-8"
              className="text-left text-sm"
              dropdownMaxWidth={320}
              dropdownFitContent
            />
          </div>
        </div>
      )}
      {leagueId ? (
        <div className="rounded-xl mb-4  w-full">
          <Table team1Id={0} team2Id={0} leagueId={leagueId} />
        </div>
      ) : (
        <div className="p-4 text-font-600 dark:text-font-600-dark">
          {translate("loading", l)}
        </div>
      )}
    </div>
  );
};

type LeagueMatchesViewProps = {
  isVisible: boolean;
  isCup: boolean;
  leagueSelectorItemsWithAll: { key: number | "all"; label: string }[];
  selectedCupLeagueId: number | "all" | undefined;
  leagueId?: number;
  onMatchesLeagueChange: (value: number | string | undefined) => void;
  availableDateObjects: Date[];
  selectedDate?: string;
  selectedDateLabel: string;
  onPrevDate: () => void;
  onNextDate: () => void;
  onDateSelect: (date: Date) => void;
  matchday: number;
  onMatchdayChange: (value: number | undefined) => void;
  matchdayItems: { key: number; label: string }[];
  displayMatches: MatchSmallElement[];
  l: LanguageLocaleEnum;
};

const LeagueMatchesView: React.FC<LeagueMatchesViewProps> = ({
                                                               isVisible,
                                                               isCup,
                                                               leagueSelectorItemsWithAll,
                                                               selectedCupLeagueId,
                                                               leagueId,
                                                               onMatchesLeagueChange,
                                                               availableDateObjects,
                                                               selectedDate,
                                                               selectedDateLabel,
                                                               onPrevDate,
                                                               onNextDate,
                                                               onDateSelect,
                                                               matchday,
                                                               onMatchdayChange,
                                                               matchdayItems,
                                                               displayMatches,
                                                               l
                                                             }) => {
  if (!isVisible) return null;
  return (
    <div>
      <div className="mb-4 flex flex-col gap-3">
        {isCup && leagueSelectorItemsWithAll.length > 1 ? (
          <>
            <div className="w-auto min-w-64">
              <LeagueSelector
                value={selectedCupLeagueId || leagueId}
                onChange={onMatchesLeagueChange}
                items={leagueSelectorItemsWithAll}
                placeholder={translate("select", l)}
                transparent
                withoutChoose
                containerClassName="relative overflow-hidden h-9 flex-shrink-0 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-8"
                className="text-left text-sm"
                dropdownMaxWidth={320}
                dropdownFitContent
              />
            </div>
            {availableDateObjects.length > 0 && selectedDate && selectedCupLeagueId === "all" && (
              <DateNavigator
                selectedDate={selectedDate}
                availableDates={availableDateObjects}
                label={selectedDateLabel}
                onPrev={onPrevDate}
                onNext={onNextDate}
                onDateSelect={onDateSelect}
              />
            )}
          </>
        ) : !isCup && (
          <div className="w-auto min-w-64">
            <MatchdaySelector
              value={matchday}
              onChange={onMatchdayChange}
              items={matchdayItems}
              placeholder={translate("select", l)}
              transparent
              withoutChoose
              containerClassName="relative overflow-hidden h-12 flex-shrink-0 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-10"
              className="text-left"
            />
          </div>
        )}
      </div>

      <RoundedRectangle
        className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4"
        hasBorder
        hasTitle
        titleType="upLeft"
        titleKey="matches"
      >
        <div className="tablet-panel-body mt-3">
          {displayMatches.map((m) => (
            <MatchTile
              key={`league-match-${m.id}`}
              match={m}
              banner="date-league"
            />
          ))}
          {displayMatches.length === 0 && (
            <div className="text-font-600 dark:text-font-600-dark p-4">
              {translate("noMatches", l)}
            </div>
          )}
        </div>
      </RoundedRectangle>
    </div>
  );
};

type LeagueTotwViewProps = {
  isVisible: boolean;
  hasTotwRestriction: boolean;
  currentMatchdayLabel: string;
  matchday: number;
  onMatchdayChange: (value: number | undefined) => void;
  totwMatchdayItems: { key: number; label: string }[];
  l: LanguageLocaleEnum;
  leagueId?: number;
};

const LeagueTotwView: React.FC<LeagueTotwViewProps> = ({
                                                         isVisible,
                                                         hasTotwRestriction,
                                                         currentMatchdayLabel,
                                                         matchday,
                                                         onMatchdayChange,
                                                         totwMatchdayItems,
                                                         l,
                                                         leagueId
                                                       }) => {
  if (!isVisible) return null;
  return (
    <div>
      <RoundedRectangle
        className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
        hasBorder
        hasTitle={false}
      >
        <div className="py-4 px-4 text-left">
          <h2 className="text-base font-medium text-font dark:text-font-dark">Team of the Week</h2>
        </div>
        <div className="p-4">
          <div className="flex justify-start">
            <div className="w-auto">
              {hasTotwRestriction ? (
                <div
                  className="relative overflow-hidden h-12 flex-shrink-0 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark px-3 text-left text-font dark:text-font-dark">
                  <span className="truncate">{currentMatchdayLabel || translate("matchDay", l)}</span>
                </div>
              ) : (
                <MatchdaySelector
                  value={matchday}
                  onChange={onMatchdayChange}
                  items={totwMatchdayItems}
                  placeholder={translate("select", l)}
                  transparent
                  withoutChoose
                  containerClassName="relative overflow-hidden h-12 flex-shrink-0 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-10"
                  className="text-left"
                />
              )}
            </div>
          </div>
        </div>

        {leagueId && (
          <TOTW leagueId={leagueId} matchDay={matchday} />
        )}
      </RoundedRectangle>
    </div>
  );
};

type LeagueStatsViewProps = {
  isVisible: boolean;
  l: LanguageLocaleEnum;
};

const LeagueStatsView: React.FC<LeagueStatsViewProps> = ({ isVisible, l }) => {
  if (!isVisible) return null;
  return (
    <div>
      <RoundedRectangle
        className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
        hasBorder
        hasTitle
        titleType="upCenter"
        titleKey="statistics"
      >
        <StoreBanner border={false} message={translate("downloadUnlimited", l)} />
      </RoundedRectangle>
    </div>
  );
};
